/**
 * Flow editor — Web Audio playback engine (sample-accurate master clock).
 *
 * The industry standard for rhythm games is to drive everything off the audio
 * hardware/DSP clock — `AudioContext.currentTime` on the web — NOT the frame
 * clock or a media element's reported `currentTime` (which is coarse, jittery,
 * and unreliable for VBR MP3s). See web.dev "A tale of two clocks".
 *
 * So instead of an <audio> element, we decode the track to an AudioBuffer and
 * play it through an AudioBufferSourceNode on the SAME AudioContext the hit
 * sounds use. The playback position is computed from the audio clock:
 *
 *     position = startOffset + (ctx.currentTime - ctxStartTime) * rate
 *
 * That value is read every animation frame to scroll the highway/timeline, so
 * the visuals are locked to what's actually audible — and the hit SFX, sharing
 * the same context, line up too.
 */

import { getFlowAudioContext } from './flowSounds';

let buffer: AudioBuffer | null = null;
let source: AudioBufferSourceNode | null = null;
let gain: GainNode | null = null;
// Persistent analyser tap for the visualizer's audio reactivity. Each new
// buffer source connects to it (in parallel with the audible gain path), so the
// spectrum follows the song the user actually hears.
let analyser: AnalyserNode | null = null;

let playing = false;
let rate = 1;
let volume = 0.8;

// Clock anchors: the song position at the moment playback (re)started, and the
// AudioContext time at that moment.
let startOffset = 0;   // seconds into the song
let ctxStartTime = 0;  // ctx.currentTime when the current source started

let onEndedCb: (() => void) | null = null;

function ensureGain(ctx: AudioContext): GainNode {
  if (!gain) {
    gain = ctx.createGain();
    gain.gain.value = volume;
    gain.connect(ctx.destination);
  }
  return gain;
}

function ensureAnalyser(ctx: AudioContext): AnalyserNode {
  if (!analyser) {
    analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0.8;
  }
  return analyser;
}

/** The analyser tapping the playing song, for the visualizer's audio reactivity.
 *  Null until the first play() has created it. */
export function getAnalyser(): AnalyserNode | null {
  return analyser;
}

let _flowFreqData: Uint8Array | null = null;
/** Live byte frequency data for the playback-bar spectrum widget. */
export function getFlowFrequencyData(): Uint8Array | null {
  if (!analyser) return null;
  if (!_flowFreqData || _flowFreqData.length !== analyser.frequencyBinCount) {
    _flowFreqData = new Uint8Array(analyser.frequencyBinCount);
  }
  analyser.getByteFrequencyData(_flowFreqData);
  return _flowFreqData;
}

function stopSource(): void {
  if (source) {
    try { source.onended = null; source.stop(); } catch { /* already stopped */ }
    try { source.disconnect(); } catch { /* ignore */ }
    source = null;
  }
}

/** Decode an audio URL into the playback buffer. Returns its exact duration. */
export async function loadAudioBuffer(url: string): Promise<number> {
  const ctx = getFlowAudioContext();
  const bytes = await (await fetch(url)).arrayBuffer();
  // decodeAudioData detaches the ArrayBuffer; hand it a private copy.
  buffer = await ctx.decodeAudioData(bytes.slice(0));
  _peaks = null; // invalidate cached waveform peaks for the new track
  startOffset = Math.min(startOffset, buffer.duration);
  return buffer.duration;
}

// ── Waveform peaks ────────────────────────────────────────────────────────────
// Downsampled amplitude envelope of the loaded track, used to draw a static
// waveform alongside the 3D timeline. Computed once per track and cached.
let _peaks: Float32Array | null = null;
let _peaksBuckets = 0;

/** Return `buckets` normalised (0..1) peak-amplitude samples across the whole
 *  track, or null if no audio is loaded. Cached per bucket count. */
export function getPeaks(buckets: number): Float32Array | null {
  if (!buffer || buckets <= 0) return null;
  if (_peaks && _peaksBuckets === buckets) return _peaks;

  const ch = buffer.getChannelData(0); // first channel is representative enough
  const out = new Float32Array(buckets);
  const per = Math.max(1, Math.floor(ch.length / buckets));
  let max = 0;
  for (let b = 0; b < buckets; b++) {
    const start = b * per;
    const end = Math.min(start + per, ch.length);
    let peak = 0;
    for (let i = start; i < end; i++) { const a = Math.abs(ch[i]); if (a > peak) peak = a; }
    out[b] = peak;
    if (peak > max) max = peak;
  }
  if (max > 0) for (let b = 0; b < buckets; b++) out[b] /= max;
  _peaks = out;
  _peaksBuckets = buckets;
  return out;
}

export function hasBuffer(): boolean { return buffer != null; }
export function getDuration(): number { return buffer?.duration ?? 0; }
export function isPlaying(): boolean { return playing; }

/** Current song position in seconds, from the audio clock (the sample being
 *  PROCESSED now). Use this for SCHEDULING (it's the same domain as
 *  getCtxTimeForSong). For the visual playhead use getHeardPosition(). */
export function getPosition(): number {
  if (!playing) return startOffset;
  const ctx = getFlowAudioContext();
  const pos = startOffset + (ctx.currentTime - ctxStartTime) * rate;
  const dur = getDuration();
  return dur > 0 ? Math.min(pos, dur) : pos;
}

/**
 * Estimated hardware output latency in seconds — the delay between a sample
 * reaching the AudioDestinationNode and actually leaving the speakers/Bluetooth.
 * Auto-detected from the Web Audio API; 0 when unavailable (e.g. Safari/iOS).
 */
export function getOutputLatency(): number {
  const ctx = getFlowAudioContext();
  const ol = (ctx as Partial<AudioContext>).outputLatency;
  if (typeof ol === 'number' && isFinite(ol) && ol >= 0) return ol;
  const bl = ctx.baseLatency;
  return (typeof bl === 'number' && isFinite(bl)) ? bl : 0;
}

/**
 * Song position (seconds) of the sample CURRENTLY BEING HEARD — i.e. reaching
 * the user's ears — which is the correct clock to drive the visual playhead so
 * the highway lines up with what's audible. Prefers AudioContext.getOutputTimestamp()
 * (extrapolated to "now" via its performanceTime pair); falls back to
 * currentTime − outputLatency, then to the plain processed position. The hit
 * SFX + metronome are scheduled in the same output pipeline, so they stay
 * aligned with both the music and this playhead at the speaker.
 */
// ── Smoothed monotonic playhead ──────────────────────────────────────────────
// getOutputTimestamp() snapshots come from the audio thread on its own cadence
// (~every render quantum) with scheduling jitter, so the raw extrapolation is
// NOT monotonic: a fresh snapshot can land BEHIND the value extrapolated from
// the previous one (measured live: thousands of backward steps per second).
// Driving the 3D highway off that raw clock made the whole track hiccup and
// visibly wobble forward/back (user report). The visual clock therefore
// advances by wall time and gently SERVOS toward the audio clock — it never
// reverses over small disagreements; only a real discontinuity (seek /
// underrun, >250ms) snaps it.
let _heardSm = 0;          // smoothed position (song seconds)
let _heardWall = 0;        // performance.now() of the last sample
let _heardEpoch = -1;      // ctxStartTime the smoothing state belongs to

export function getHeardPosition(): number {
  if (!playing) return startOffset;
  const ctx = getFlowAudioContext();
  // Fallback: the processed clock shifted back by the device output latency.
  let ctxTime = ctx.currentTime - getOutputLatency();
  const getTS = (ctx as Partial<AudioContext>).getOutputTimestamp;
  if (typeof getTS === 'function') {
    try {
      const ts = getTS.call(ctx);
      if (ts && typeof ts.contextTime === 'number' && ts.contextTime > 0
          && typeof ts.performanceTime === 'number') {
        // Extrapolate the sampled (contextTime, performanceTime) pair to now;
        // the output device advances in real time (1:1 with the ctx clock).
        // NOTE: performanceTime is in MILLISECONDS (same as performance.now()),
        // contextTime is in SECONDS — convert the delta to seconds.
        ctxTime = ts.contextTime + (performance.now() - ts.performanceTime) / 1000;
      }
    } catch { /* getOutputTimestamp not usable → keep fallback */ }
  }
  const pos = startOffset + (ctxTime - ctxStartTime) * rate;
  const dur = getDuration();
  const raw = Math.max(0, dur > 0 ? Math.min(pos, dur) : pos);

  const now = performance.now();
  // New run (play/seek/rate change re-anchors ctxStartTime) → adopt raw as-is.
  if (_heardEpoch !== ctxStartTime) {
    _heardEpoch = ctxStartTime;
    _heardSm = raw;
    _heardWall = now;
    return raw;
  }
  const dt = Math.max(0, (now - _heardWall) / 1000);
  _heardWall = now;
  const predicted = _heardSm + dt * rate;   // wall-clock advance — always forward
  const err = raw - predicted;
  if (Math.abs(err) > 0.25) {
    _heardSm = raw;                          // discontinuity → snap
  } else {
    // Gentle servo toward the audio clock, floored so the correction can
    // never outweigh the frame's forward advance (no backward steps, ever).
    _heardSm = Math.max(_heardSm, predicted + err * 0.08);
  }
  return dur > 0 ? Math.min(_heardSm, dur) : _heardSm;
}

/**
 * Convert a song position (seconds) to the AudioContext time at which it
 * plays, so callers (e.g. the metronome) can SCHEDULE clicks ahead on the same
 * sample-accurate clock as the music. Only meaningful while playing; returns
 * the current ctx time otherwise.
 */
export function getCtxTimeForSong(songSec: number): number {
  const ctx = getFlowAudioContext();
  if (!playing) return ctx.currentTime;
  return ctxStartTime + (songSec - startOffset) / rate;
}

/** Start (or restart) playback from `fromOffset` seconds (defaults to the
 *  current position). */
export function play(fromOffset?: number): void {
  const ctx = getFlowAudioContext();
  if (ctx.state === 'suspended') ctx.resume();
  if (!buffer) return;

  stopSource();
  const g = ensureGain(ctx);

  const src = ctx.createBufferSource();
  src.buffer = buffer;
  src.playbackRate.value = rate;
  src.connect(g);
  // Parallel tap for the visualizer (does not affect the audible path).
  src.connect(ensureAnalyser(ctx));

  const off = Math.max(0, Math.min(fromOffset ?? startOffset, buffer.duration));
  startOffset = off;
  ctxStartTime = ctx.currentTime;

  src.onended = () => {
    // Fires on natural end (we null it out before manual stops). Only treat as
    // "ended" if we actually reached the tail.
    if (playing && getPosition() >= getDuration() - 0.06) {
      playing = false;
      startOffset = getDuration();
      onEndedCb?.();
    }
  };

  source = src;
  src.start(0, off);
  playing = true;
}

export function pause(): void {
  if (!playing) return;
  const pos = getPosition();
  stopSource();
  playing = false;
  startOffset = pos;
}

/** Move the playhead. Keeps playing if currently playing. */
export function seek(t: number): void {
  const dur = getDuration();
  const clamped = Math.max(0, dur > 0 ? Math.min(t, dur) : t);
  if (playing) play(clamped);
  else startOffset = clamped;
}

export function setVolume(v: number): void {
  volume = Math.max(0, Math.min(1, v));
  if (gain) gain.gain.value = volume;
}

export function setRate(r: number): void {
  if (r <= 0 || r === rate) { rate = r > 0 ? r : rate; return; }
  // Re-anchor so the position stays continuous across the rate change.
  const pos = getPosition();
  rate = r;
  if (playing) play(pos);
}

export function setOnEnded(cb: (() => void) | null): void { onEndedCb = cb; }

/** Tear down for a fresh track / unmount. */
export function reset(): void {
  stopSource();
  playing = false;
  startOffset = 0;
  buffer = null;
}
